import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { placeholderProfilePicture } from 'common/apartmentLocatorHelpers';

import { USER_ROLE_APARTMENT_LOCATOR, USER_ROLE_ADMIN } from 'common/userRoles';

import { SCREEN_ID_AGENT_PROFILE } from 'client/constants/screenIds';
import {
  SR_API_ACTION_APARTMENT_LOCATOR_TOGGLE_LEADS_ENABLED,
  SR_API_ACTION_APARTMENT_LOCATOR_GET_MAX_LEADS_STATUS,
} from 'api/actionTypes';

import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';

import ApartmentLocatorActions from 'client/actions/ApartmentLocator';

import Navbar from 'client/components/Navbar';
import SrAgentDashboardTabBar from 'client/components/SrAgentDashboardTabBar';
import SrApartmentLocatorStats from 'client/components/SrApartmentLocatorStats';
import SrFilterCheckbox from 'client/components/SrFilterCheckbox';
import SrSettingsModal from 'client/components/SrSettingsModal';
import SrAgentLeaderboardCard from 'client/components/SrAgentLeaderboardCard';

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_APARTMENT_LOCATOR, USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_AGENT_PROFILE)
@branch({
  currentUser: ['currentUser'],
  maxLeadsStatus: ['apartmentLocator', 'maxLeadsStatus'],
})
@withApiState(({ currentUser }) => ({
  toggleApartmentLeadsEnabledApiState: {
    action: SR_API_ACTION_APARTMENT_LOCATOR_TOGGLE_LEADS_ENABLED,
    queryId: String(currentUser.id),
  },
  getMaxLeadsStatusApiState: {
    action: SR_API_ACTION_APARTMENT_LOCATOR_GET_MAX_LEADS_STATUS,
  },
}))
class ApartmentLocatorProfilePage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Profile | Sunroom Rentals',
  });

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    toggleApartmentLeadsEnabledApiState: PropTypes.object.isRequired,
    getMaxLeadsStatusApiState: PropTypes.object.isRequired,
    maxLeadsStatus: PropTypes.object,
  };

  static fetchData(match, inject) {
    return ApartmentLocatorActions.getMaxLeadsStatus(inject);
  }

  constructor(props) {
    super(props);

    this.state = {
      showSettingsModal: false,
    };
  }

  _onToggleLeadsEnabledClick = () => {
    const { currentUser, dispatch } = this.props;
    dispatch(ApartmentLocatorActions.toggleApartmentLeadsEnabled, {
      userId: currentUser.id,
      apartmentLeadsEnabled: !currentUser.apartmentLeadsEnabled,
    });
  };

  _onSettingsClick = () => {
    this.setState({
      showSettingsModal: true,
    });
  };

  _onCloseSettingsModal = () => {
    this.setState({
      showSettingsModal: false,
    });
  };

  render() {
    const {
      currentUser,
      maxLeadsStatus,
      getMaxLeadsStatusApiState,
      toggleApartmentLeadsEnabledApiState,
    } = this.props;

    const { showSettingsModal } = this.state;

    const switchDisabled =
      currentUser.apartmentLeadsSwitchDisabled ||
      (maxLeadsStatus && maxLeadsStatus.maxLeadsReached);
    const leadsEnabled =
      currentUser.apartmentLeadsEnabled && maxLeadsStatus && !maxLeadsStatus.maxLeadsReached;

    return (
      <div className="page-agentDashboard profile">
        <Navbar
          redirectLogoTo={getUserLandingPage({ userRoles: currentUser.roles })}
          rightButton={<span className="settingsGear" onClick={this._onSettingsClick} />}
        />
        <SrAgentDashboardTabBar />
        <div className="contentContainer">
          <div className="bioContainer">
            <div className="avatarContainer">
              <img
                className="apartmentLocatorProfilePicture"
                src={currentUser.profilePicture || placeholderProfilePicture}
                alt={currentUser.name}
              />
            </div>
            <div className="textContainer">
              <span className="welcome">{currentUser.name}</span>
            </div>
          </div>
          <div className="cardsContainer">
            <SrApartmentLocatorStats />
            <SrAgentLeaderboardCard userRole={USER_ROLE_APARTMENT_LOCATOR} />
          </div>
          <div className="actionsContainer">
            {!getMaxLeadsStatusApiState.inProgress &&
              currentUser.roles.includes(USER_ROLE_APARTMENT_LOCATOR) && (
                <div
                  className={classNames('apartmentLocatorSettings', {
                    disabled: switchDisabled,
                  })}
                >
                  <div className="row switch">
                    <span className="title">New Apartment Leads</span>
                    <SrFilterCheckbox
                      id="apartmentLeadsEnabled"
                      onChange={this._onToggleLeadsEnabledClick}
                      value={
                        toggleApartmentLeadsEnabledApiState.inProgress
                          ? !leadsEnabled
                          : leadsEnabled
                      }
                      disabled={toggleApartmentLeadsEnabledApiState.inProgress || switchDisabled}
                    />
                  </div>
                  {switchDisabled && (
                    <div className="row disableLeadsExplanation">
                      <div>
                        <span>Your leads have been turned off temporarily.</span>
                      </div>
                      <div>
                        <span>
                          {maxLeadsStatus && maxLeadsStatus.maxLeadsReached
                            ? `You have reached your limit of ${maxLeadsStatus.maxLeadsAmount} ` +
                              `leads in the past ${maxLeadsStatus.maxLeadsDays} days.`
                            : 'Please contact Zac to get your leads turned back on.'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>

        {showSettingsModal && (
          <SrSettingsModal isOpen={showSettingsModal} onClose={this._onCloseSettingsModal} />
        )}
      </div>
    );
  }
}

ApartmentLocatorProfilePage.displayName = 'ApartmentLocatorProfilePage';

export default ApartmentLocatorProfilePage;
